(() => {
  const DEFAULTS = {
    maxVisibleSwatches: 5,
    selectors: {
      card: '.product-card',
      swatchSwiper: '.swatch-swiper',
      swatchSlide: '.swatch-slide',
      productImageSwiper: '.product-image-swiper',
      productLink: '.product-link',
    },
  };

  const getFilteredColorFromUrl = () => {
    try {
      const params = new URLSearchParams(window.location.search);
      return (
        params.get('filter.v.option.color') ||
        params.get('filter.v.option.Farbe') ||
        null
      );
    } catch {
      return null;
    }
  };

  const toLower = (v) => (typeof v === 'string' ? v.toLowerCase() : v);

  // Color slides other than the opening one ship with data-src (see product-card-color-slides-and-swatches)
  const hydrateSlide = (slide) => {
    const img = slide?.querySelector('img[data-src]');
    if (!img) return;
    img.src = img.dataset.src;
    img.removeAttribute('data-src');
  };

  const hydrateAround = (slides, index) => {
    for (let i = index - 1; i <= index + 1; i++) hydrateSlide(slides[i]);
  };

  const updateProductLink = (card, variantUrl, selectors) => {
    if (!variantUrl) return;
    const links = card.querySelectorAll(selectors.productLink);
    links.forEach((link) => link.setAttribute('href', variantUrl));
  };

  const updateVariantDiscountBadge = (card, activeSlide) => {
    if (!activeSlide) return;

    const discountBadge = card.querySelector('[data-variant-discount-badge]');
    if (!discountBadge) return;

    const badgeInner = card.querySelector('[data-variant-discount-badge-inner]');
    const badgeValue = card.querySelector('[data-variant-discount-badge-value]');
    const discountPercentage = parseInt(activeSlide.dataset.discountPercentage || '0', 10);
    const hasDiscount = !Number.isNaN(discountPercentage) && discountPercentage > 0;

    discountBadge.classList.toggle('hidden', !hasDiscount);
    if (badgeValue) badgeValue.textContent = hasDiscount ? `${discountPercentage}%` : '';
    if (!badgeInner) return;

    badgeInner.style.backgroundColor = hasDiscount ? (activeSlide.dataset.discountBgColor || '') : '';
    badgeInner.style.color = hasDiscount ? (activeSlide.dataset.discountTextColor || '') : '';
  };

  const updateVariantSaleIcon = (card, activeSlide) => {
    if (!activeSlide) return;

    const saleIcon = card.querySelector('[data-variant-sale-icon]');
    if (!saleIcon) return;

    const discountPercentage = parseInt(activeSlide.dataset.discountPercentage || '0', 10);
    const hasDiscount = !Number.isNaN(discountPercentage) && discountPercentage > 0;
    saleIcon.classList.toggle('hidden', !hasDiscount);
  };

  const syncCardVariantUi = (card, activeSlide, selectors) => {
    if (!activeSlide) return;

    const color = activeSlide.dataset.color;
    const variantUrl = activeSlide.dataset.url;

    updateProductLink(card, variantUrl, selectors);
    updateVariantDiscountBadge(card, activeSlide);
    updateVariantSaleIcon(card, activeSlide);

    card.querySelectorAll(`${selectors.swatchSwiper} ${selectors.swatchSlide}`).forEach((s) => {
      const match = (s.dataset.color === color) || (s.dataset.value === color);
      s.classList.toggle('active-swatch', match);
    });
  };

  // IntersectionObserver for deferred Swiper initialization
  const swiperObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting && !entry.target.dataset.swatchesInitialized) {
        // Retrieve stored options if any were passed
        const options = entry.target.dataset.swatchesOptions ? JSON.parse(entry.target.dataset.swatchesOptions) : {};
        initCardDeferred(entry.target, options);
        swiperObserver.unobserve(entry.target);
        delete entry.target.dataset.swatchesOptions;
      }
    });
  }, { rootMargin: '200px' });

  const initCardDeferred = (card, options = {}) => {
    if (!card || card.dataset.swatchesInitialized === 'true') return;
    const cfg = {
      maxVisibleSwatches: options.maxVisibleSwatches ?? DEFAULTS.maxVisibleSwatches,
      selectors: { ...DEFAULTS.selectors, ...(options.selectors || {}) },
    };

    const { selectors } = cfg;

    // Swiper must be on window
    if (typeof window.Swiper !== 'function') {
      // Try again soon if Swiper not yet loaded
      setTimeout(() => initCardDeferred(card, options), 50);
      return;
    }

    const swatchSwiperEl = card.querySelector(selectors.swatchSwiper);
    const imageSwiperEl = card.querySelector(selectors.productImageSwiper);
    if (!imageSwiperEl) {
      card.dataset.swatchesInitialized = 'true';
      return;
    }

    const imageSlideEls = Array.from(imageSwiperEl.querySelectorAll('.swiper-slide'));

    if (!swatchSwiperEl) {
      const active = imageSlideEls[0];
      if (active) syncCardVariantUi(card, active, selectors);
      card.dataset.swatchesInitialized = 'true';
      return;
    }

    // Preselection from Liquid (data-initial-index) or URL filter – used for both swipers
    const filteredColor = getFilteredColorFromUrl();
    let initialIdx = 0;
    const initialIndex = card.dataset.initialIndex;
    if (filteredColor) {
      const lc = toLower(filteredColor);
      const found = imageSlideEls.findIndex((s) => toLower(s.dataset.color) === lc);
      if (found >= 0) initialIdx = found;
    } else if (initialIndex !== undefined && initialIndex !== "") {
      const n = parseInt(initialIndex, 10);
      if (!Number.isNaN(n) && n >= 0 && n < imageSlideEls.length) initialIdx = n;
    }

    // Create swatch swiper (initialSlide so strip starts at preselected color)
    const lockInitialSwipe = (card.dataset?.swatchesLockInitial === 'true') || (card.dataset?.initialSwatchLocked === 'true');
    const swatchSwiper = new Swiper(swatchSwiperEl, {
      slidesPerView: 'auto',
      freeMode: true,
      watchSlidesProgress: true,
      initialSlide: initialIdx,
      allowTouchMove: !lockInitialSwipe,
    });

    const swatchSlides = card.querySelectorAll(`${selectors.swatchSwiper} ${selectors.swatchSlide}`);
    let extraSlides = [];

    if (swatchSlides.length > cfg.maxVisibleSwatches) {
      const toRemove = Array.from({ length: swatchSlides.length - cfg.maxVisibleSwatches }, (_, i) => i + cfg.maxVisibleSwatches);
      extraSlides = toRemove.map((idx) => swatchSlides[idx]);
      swatchSwiper.removeSlide(toRemove);

      const remaining = extraSlides.length;
      const moreBtn = document.createElement('div');
      moreBtn.className = 'text-xs text-secondaryhighlight cursor-pointer more-button';
      moreBtn.innerHTML = `<span>+${remaining}</span>`;
      const swatchWrapper = card.querySelector('.swatch-wrapper');
      if (swatchWrapper) swatchWrapper.appendChild(moreBtn);

      moreBtn.addEventListener('click', () => {
        extraSlides.forEach((slide) => swatchSwiper.appendSlide(slide));
        moreBtn.remove();
        try { swatchSwiper.allowTouchMove = true; } catch { }
        swatchSwiper.update();
        swatchSwiper.slideTo(swatchSwiper.slides.length - 1);
      });
    }

    // Create image swiper (initialSlide from Liquid/URL so correct slide from first paint)
    const prevButton = imageSwiperEl.querySelector('.swiper-button-prev');
    const nextButton = imageSwiperEl.querySelector('.swiper-button-next');
    const disableImageSliderTouch = card.dataset?.imageSliderTouchDisabled === 'true';

    const imageSwiper = new Swiper(imageSwiperEl, {
      slidesPerView: 1,
      spaceBetween: 0,
      initialSlide: initialIdx,
      allowTouchMove: !disableImageSliderTouch,
      lazy: true,
      preloadImages: false,
      lazyPreloadPrevNext: 1,
      watchSlidesProgress: true,
      navigation: prevButton && nextButton ? {
        nextEl: nextButton,
        prevEl: prevButton,
        disabledClass: 'swiper-button-disabled',
      } : false,
    });

    hydrateAround(imageSwiper.slides, imageSwiper.activeIndex);

    // Preload a color's image as soon as its swatch is hovered/touched, before the click
    swatchSwiperEl.addEventListener('pointerover', (evt) => {
      const slide = evt.target.closest(selectors.swatchSlide);
      if (!slide) return;
      const color = slide.dataset.color || slide.dataset.value;
      hydrateSlide(Array.from(imageSwiper.slides).find((s) => s.dataset.color === color));
    }, { passive: true });

    // Bind swatch clicks via delegation so dynamically appended slides work
    swatchSwiperEl.addEventListener('click', (evt) => {
      const slide = evt.target.closest(selectors.swatchSlide);
      if (!slide || !swatchSwiperEl.contains(slide)) return;
      const color = slide.dataset.color || slide.dataset.value;
      const idx = Array.from(imageSwiper.slides).findIndex((s) => s.dataset.color === color);
      if (idx !== -1) {
        imageSwiper.slideTo(idx);
        card
          .querySelectorAll(`${selectors.swatchSwiper} ${selectors.swatchSlide}`)
          .forEach((s) => s.classList.remove('active-swatch'));
        slide.classList.add('active-swatch');
      }
    });

    // Sync on image slide change
    imageSwiper.on('slideChange', () => {
      hydrateAround(imageSwiper.slides, imageSwiper.activeIndex);
      const active = imageSwiper.slides[imageSwiper.activeIndex];
      if (!active) return;
      syncCardVariantUi(card, active, selectors);
    });

    // Initial state: set product link and swatch active from preselected slide (already at initialSlide)
    const setInitialState = () => {
      if (!imageSwiper.slides.length) return;
      const idx = imageSwiper.activeIndex;
      const active = imageSwiper.slides[idx];
      syncCardVariantUi(card, active, selectors);

      const activeColor = active?.dataset?.color;
      const slides = Array.from(card.querySelectorAll(`${selectors.swatchSwiper} ${selectors.swatchSlide}`));
      slides.forEach((s, i) => {
        const matchColor = s.dataset.color || s.dataset.value;
        const isActive = matchColor && activeColor && matchColor === activeColor;
        s.classList.toggle('active-swatch', isActive);
        if (isActive) {
          try { swatchSwiper.slideTo(i, 0); } catch { }
        }
      });
    };

    setInitialState();

    // Ensure Swiper updates navigation state after initialization
    // This handles cases where slides might load lazily or navigation needs recalculation
    if (imageSwiper && prevButton && nextButton) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        try {
          imageSwiper.update();
          imageSwiper.updateSlidesClasses();
        } catch { }
      });
    }

    card.dataset.swatchesInitialized = 'true';
  };

  const initCard = (card, options = {}) => {
    if (!card || card.dataset.swatchesInitialized === 'true' || card.dataset.swatchesObserved === 'true') return;
    card.dataset.swatchesObserved = 'true';
    // Store options on the element for retrieval when observer fires
    if (options && Object.keys(options).length > 0) {
      card.dataset.swatchesOptions = JSON.stringify(options);
    }
    // Observe for viewport entry instead of immediate initialization
    swiperObserver.observe(card);
  };

  const initAll = (root = document, options = {}) => {
    const cfg = { ...DEFAULTS, ...(options || {}) };
    const cards = root.querySelectorAll(cfg.selectors.card);
    cards.forEach((card) => initCard(card, options));
  };

  // Fallback for card swipers initialised elsewhere: interacting with a card loads all its color slides
  const hydrateCard = (evt) => {
    const swiperEl = evt.target.closest?.('.product-image-swiper');
    if (!swiperEl) return;
    swiperEl.querySelectorAll('.swiper-slide').forEach(hydrateSlide);
  };
  document.addEventListener('touchstart', hydrateCard, { passive: true, capture: true });
  document.addEventListener('focusin', hydrateCard);

  window.ProductCardSwatches = {
    initCard,
    initAll,
  };

  // Backward compatibility alias
  window.initProductCard = initCard;
})();
